import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import type { Data, Layout } from "plotly.js";
import { parseLocalDateTime } from "./parseLocalDateTime";
import { toLocalCalendarTime } from "./toLocalCalendarTime";

dayjs.extend(utc);
dayjs.extend(timezone);

export const plotlyUtcDate = <T>(coordinate: T): T | string =>
  coordinate instanceof Date ? coordinate.toISOString() : coordinate;

export const plotlyUtcMilliseconds = (coordinate: unknown): number =>
  typeof coordinate === "number"
    ? coordinate
    : typeof coordinate === "string" || coordinate instanceof Date
      ? dayjs.utc(coordinate).valueOf()
      : NaN;

export const formatPlotlyTime = ({
  date,
  timeZone,
}: {
  date: Date;
  timeZone: string;
}): string => {
  const timestamp = dayjs.utc(date);
  const calendarTime = toLocalCalendarTime(timestamp, timeZone);
  return `${calendarTime.format("YYYY-MM-DD HH:mm:ss")} ${timestamp.tz(timeZone).format("Z")}`;
};

const tickIntervals = [
  [1, "millisecond"],
  [100, "millisecond"],
  [500, "millisecond"],
  [1, "second"],
  [5, "second"],
  [15, "second"],
  [30, "second"],
  [1, "minute"],
  [5, "minute"],
  [15, "minute"],
  [30, "minute"],
  [1, "hour"],
  [3, "hour"],
  [6, "hour"],
  [12, "hour"],
  [1, "day"],
  [7, "day"],
  [1, "month"],
  [3, "month"],
  [1, "year"],
] as const;

export const getPlotlyDateTicks = ({
  start,
  end,
  timeZone,
}: {
  start: Date;
  end: Date;
  timeZone: string;
}) => {
  const first = dayjs.utc(start);
  const last = dayjs.utc(end);
  const [step, unit] = tickIntervals.find(
    ([count, interval]) =>
      first.add(count, interval).diff(first) >= last.diff(first) / 5
  ) ?? [Math.max(1, Math.ceil(last.diff(first, "year", true) / 5)), "year"];
  const localStart = toLocalCalendarTime(first, timeZone);
  const calendarScale = unit === "day" || unit === "month" || unit === "year";
  let coordinates: number[];
  if (calendarScale) {
    const calendarStart = localStart;
    const calendarEnd = toLocalCalendarTime(last, timeZone);
    const field = unit === "day" ? "date" : unit;
    const position = calendarStart.get(field) - (unit === "day" ? 1 : 0);
    const boundary = calendarStart
      .startOf(unit)
      .subtract(position % step, unit);
    coordinates = Array.from(
      {
        length: Math.max(
          0,
          Math.ceil(calendarEnd.diff(boundary, unit, true) / step) + 1
        ),
      },
      (_, index) =>
        parseLocalDateTime(boundary.add(index * step, unit), timeZone).valueOf()
    );
  } else {
    const interval = first.add(step, unit).diff(first);
    const offsets = [
      ...new Set([
        first.tz(timeZone).utcOffset(),
        last.tz(timeZone).utcOffset(),
      ]),
    ];
    coordinates = offsets.flatMap((offset) => {
      const offsetMilliseconds = offset * 60_000;
      const boundary =
        Math.ceil((first.valueOf() + offsetMilliseconds) / interval) *
          interval -
        offsetMilliseconds;
      return Array.from(
        {
          length: Math.max(
            0,
            Math.floor((last.valueOf() - boundary) / interval) + 1
          ),
        },
        (_, index) => boundary + index * interval
      ).filter(
        (timestamp) => dayjs.utc(timestamp).tz(timeZone).utcOffset() === offset
      );
    });
  }
  const ticks = [...new Set(coordinates)]
    .filter(
      (timestamp) => timestamp >= first.valueOf() && timestamp <= last.valueOf()
    )
    .sort((left, right) => left - right);
  return {
    tickvals: ticks.map((timestamp) => dayjs.utc(timestamp).toISOString()),
    ticktext: ticks.map((timestamp) => {
      const calendarTime = toLocalCalendarTime(dayjs.utc(timestamp), timeZone);
      return calendarTime.format(
        calendarScale
          ? "YYYY-MM-DD"
          : calendarTime.millisecond()
            ? "YYYY-MM-DD<br>HH:mm:ss.SSS"
            : calendarTime.second()
              ? "YYYY-MM-DD<br>HH:mm:ss"
              : "YYYY-MM-DD<br>HH:mm"
      );
    }),
  };
};

export const preparePlotlyDateTrace = ({
  trace,
  timeZone,
  hovermode,
}: {
  trace: Data;
  timeZone: string;
  hovermode: Partial<Layout>["hovermode"];
}): Data => {
  if (!("x" in trace) || !Array.isArray(trace.x)) return trace;
  const coordinates = trace.x;
  if (!coordinates.some((coordinate) => coordinate instanceof Date))
    return trace;

  const hoverinfo = "hoverinfo" in trace ? trace.hoverinfo : undefined;
  const sourceTemplate =
    "hovertemplate" in trace ? trace.hovertemplate : undefined;
  const hoverFields = (
    hoverinfo && hoverinfo !== "all" ? hoverinfo : "x+y+text+name"
  ).split("+");
  const preserveHover = ["text", "skip", "none"].includes(hoverinfo ?? "");
  const hovertemplate = preserveHover
    ? sourceTemplate
    : coordinates.map((coordinate, index) => {
        const template = Array.isArray(sourceTemplate)
          ? sourceTemplate[index]
          : sourceTemplate;
        if (!(coordinate instanceof Date)) return template ?? "";
        const timeLabels =
          hovermode === "x unified"
            ? []
            : [formatPlotlyTime({ date: coordinate, timeZone })];
        if (template) return [...timeLabels, template].join("<br>");
        const textField = trace.hovertext != null ? "hovertext" : "text";
        const parts = [
          ...timeLabels,
          ...(hoverFields.includes("y") ? ["%{y}"] : []),
          ...(hoverFields.includes("text") && trace[textField] != null
            ? [`%{${textField}}`]
            : []),
        ];
        const extra = hoverFields.includes("name") ? "%{fullData.name}" : "";
        return `${parts.join("<br>")}<extra>${extra}</extra>`;
      });

  return { ...trace, x: coordinates.map(plotlyUtcDate), hovertemplate } as Data;
};
